import CustomCard from '@/components/customCard';
import { BoxIcon, CarIcon } from '@/components/Icons';
import CartPlus from '@/components/Icons/CartPlus';
import CursorClick from '@/components/Icons/CursorClick';
import CursorClickedIcon from '@/components/Icons/CursorClicked';
import CursorHoverIcon from '@/components/Icons/CursorHover';
import EyeIcon from '@/components/Icons/Eye';
import ReactionIcon from '@/components/Icons/ReactionIcon';
import UserIcon from '@/components/Icons/User';
import RegularText from '@/components/RegularText';
import { BREAKPOINT } from '@/constants/enum';
import { PATH } from '@/constants/path';
import useUserPlan from '@/hooks/useUserPlan';
import { AnalyticsStyled } from '@/pages/Analytics/styled';
import { Badge, InlineGrid, Link, Tooltip } from '@shopify/polaris';
import { memo, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

export interface IDataAverage {
  addToCartRate: number;
  clickThroughRate: number;
  engagementRate: number;
  activeRules: number;
  products: number;
  PDPVisits: number;
  impressions: number;
  hovers: number;
  clicks: number;
  addToCart: number;
}

interface IProps {
  dataAverageRate: IDataAverage;
  inHomePage?: boolean;
}

const AverageRate = ({ dataAverageRate, inHomePage }: IProps) => {
  const navigate = useNavigate();
  const { isFreePlan } = useUserPlan();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.XS });
  const data = useMemo(
    () => [
      {
        id: 1,
        name: 'Add-to-Cart rate',
        value: dataAverageRate?.addToCartRate || 0,
        icon: <CartPlus />,
        toolTip: `% of visitors adding products with ETA banners to cart. ATC%=100(ATC/PDP visits)`,
      },
      {
        id: 2,
        name: 'Click-through rate',
        value: dataAverageRate?.clickThroughRate || 0,
        icon: <CursorClick />,
        toolTip: `% of clicks on ETA banner views CTR=100(Click/Impressions)`,
      },
      {
        id: 3,
        name: 'Engagement rate',
        value: dataAverageRate?.engagementRate || 0,
        icon: <ReactionIcon />,
        toolTip: `% of total interactions with ETA banners CTR=100(Hover+Click+ATC/Impressions)`,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(dataAverageRate)],
  );

  const dataTotalCount = useMemo(
    () => [
      {
        id: 1,
        name: 'Active rules',
        value: dataAverageRate?.activeRules || 0,
        icon: <CarIcon width={18} height={18} fill="#616161" />,
        toolTip: `Number of active estimate rules.`,
      },
      {
        id: 2,
        name: 'Products',
        value: dataAverageRate?.products || 0,
        icon: <BoxIcon width={18} height={18} fill="#616161" />,
        toolTip: `Number of products with ETA banners.`,
      },
      {
        id: 3,
        name: 'PDP visits',
        value: dataAverageRate?.PDPVisits || 0,
        icon: <UserIcon width={18} height={18} fill="#616161" />,
        toolTip: `Number of visitors to product pages.`,
      },
      {
        id: 4,
        name: 'Impressions',
        value: dataAverageRate?.impressions || 0,
        icon: <EyeIcon width={18} height={18} fill="#616161" />,
        toolTip: `Number of times ETA banners display.`,
      },
      {
        id: 5,
        name: 'Hovers',
        value: dataAverageRate?.hovers || 0,
        icon: <CursorHoverIcon width={18} height={18} fill="#616161" />,
        toolTip: `Number of hover interactions over ETA banners.`,
      },
      {
        id: 6,
        name: 'Clicks',
        value: dataAverageRate?.clicks || 0,
        icon: <CursorClickedIcon width={18} height={18} fill="#616161" />,
        toolTip: `Number of clicks on ETA banners.`,
      },
      {
        id: 7,
        name: 'Add to Cart',
        value: dataAverageRate?.addToCart || 0,
        icon: <CartPlus width={18} height={18} fill="#616161" />,
        toolTip: `Number of times products with ETA are added to cart.`,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(dataAverageRate)],
  );
  return (
    <CustomCard
      isResponsiveAction
      title={
        inHomePage ? (
          <div className={`d-flex gap-8 ${isMobile ? 'flex-column' : 'flex-row items-center'}`}>
            <p style={{ fontSize: '13px', fontWeight: 600 }}>Overview last 7 days</p>
            {isFreePlan ? (
              <div className={`d-flex gap-8 ${isMobile ? 'flex-column mb-8' : 'flex-row items-center'}`}>
                <div>
                  <Badge tone="info">Demo data</Badge>
                </div>
                <div onClick={() => navigate(PATH.PRICING_PLAN)} className="cursor-pointer">
                  <Badge tone="info">Standard and above</Badge>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <b className={`mt-16 ${isFreePlan ? 'disabled' : ''}`} style={{ fontSize: '16px' }}>
            Average performance rates
          </b>
        )
      }
      titleContentRight={
        inHomePage ? (
          <Link removeUnderline onClick={() => navigate(PATH.ANALYTICS)}>
            View full analytics
          </Link>
        ) : undefined
      }
    >
      <AnalyticsStyled className={`mt-16 ${isFreePlan ? 'disabled' : ''}`}>
        <InlineGrid gap="400" columns={{ xs: 1, sm: 1, md: 3 }}>
          {data.map((item) => (
            <Tooltip key={item.id} content={item.toolTip} width="wide">
              <CustomCard>
                <div className="d-flex align-center">
                  <div className="mr-16 icon-average-rate">{item.icon}</div>
                  <div className="d-flex flex-column">
                    <RegularText>{item.name}</RegularText>
                    <b style={{ fontSize: '16px' }}>{item.value}%</b>
                  </div>
                </div>
              </CustomCard>
            </Tooltip>
          ))}
        </InlineGrid>
      </AnalyticsStyled>
      {!inHomePage && (
        <div className={`mt-16 ${isFreePlan ? 'disabled' : ''}`}>
          <b style={{ fontSize: '16px' }}>Total counts</b>
          <div className="wrap-data-total-count mt-16">
            {dataTotalCount.map((item) => (
              <Tooltip key={item.id} content={item.toolTip} width="wide">
                <CustomCard>
                  <div className="d-flex flex-column">
                    <div className="mr-16 icon-average-total">{item.icon}</div>
                    <div className="d-flex flex-column mt-8">
                      <RegularText>{item.name}</RegularText>
                      <b style={{ fontSize: '16px' }}>{item.value}</b>
                    </div>
                  </div>
                </CustomCard>
              </Tooltip>
            ))}
          </div>
        </div>
      )}
    </CustomCard>
  );
};

export default memo(AverageRate);
